import { baseApiUrl } from 'src/config';
import { getAccessToken } from 'src/routes/AuthContext';

export interface AddendumNoteGeneralInformation {
  patientFirstName: string;
  patientLastName: string;
  therapistFirstName: string;
  therapistLastName: string;
  licenseNumber: string;
  credentials: string;
}

export interface AddendumNotePayload {
  generalInformation: AddendumNoteGeneralInformation;
  additionalComments: string;
}

/**
 * Submits an addendum note to the document-service.
 *
 * Backend contract:
 *   POST {baseApiUrl}/document/form/addendum-note/{formId}?webhookUuid={formSubmissionUuid}
 *
 * Follows the same fetch/header convention as submitReform.ts — the documentService/*
 * layer does not use the axios interceptor, so Authorization / x-tenant-id / Facility_id /
 * X-Forwarded-For are attached manually here.
 *
 * @param realm               tenant id (x-tenant-id header)
 * @param formId              addendum-note reform id (document.reformId, e.g. "addendum-reform-qa-001")
 * @param formSubmissionUuid  form submission uuid, sent as the webhookUuid query param
 * @param facilityId          facility id (Facility_id header)
 * @param payload             { generalInformation, additionalComments }
 */
export const submitAddendumNote = async (
  realm: string,
  formId: string,
  formSubmissionUuid: string,
  facilityId: string,
  payload: AddendumNotePayload
): Promise<{ error: boolean | string }> => {
  const token = await getAccessToken();
  const apiUrl = `${baseApiUrl}/document/form/addendum-note/${formId}?webhookUuid=${formSubmissionUuid}`;

  return fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-tenant-id': realm,
      Facility_id: facilityId,
      // Session IP for the SUBMIT audit row — same convention as submitReform.ts.
      'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
    },
    body: JSON.stringify(payload),
  })
    .then(async (response) => {
      if (response.status === 200) {
        return { error: false };
      }

      let errorMessage = 'Something went wrong';
      try {
        const errorResponse = await response.json();
        errorMessage = errorResponse.message || errorResponse.error || errorMessage;
      } catch (e) {
        errorMessage = response.statusText || `Request failed with status ${response.status}`;
      }

      return { error: errorMessage };
    })
    .catch((error) => {
      return { error: `Error submitting addendum note: ${error.message || error}` };
    });
};
